/**
 * The preview build runs as a page on claude.ai, where there is no rooms API. There the page
 * itself is the room: everyone it's shared with (with edit access) can join in, and each
 * person's swipes live in the page's shared database at people/<their user id>.
 *
 * This file only talks to the platform; partner.ts decides what to read and write.
 */

interface DbError {
  code: string;
  message: string;
}
interface DocSnap {
  id: string;
  exists: boolean;
  data(): Record<string, unknown> | undefined;
}
interface QuerySnap {
  docs: DocSnap[];
}
interface DocRef {
  get(): Promise<DocSnap>;
  set(data: Record<string, unknown>): Promise<void>;
  delete(): Promise<void>;
}
interface CollRef {
  doc(id: string): DocRef;
  get(): Promise<QuerySnap>;
  onSnapshot(next: (s: QuerySnap) => void, error?: (e: DbError) => void): () => void;
}
interface Db {
  collection(path: string): CollRef;
}
interface User {
  id(): Promise<string | null>;
  can(capability: string): Promise<boolean | null>;
}

export interface PageRoom {
  db: Db;
  /** this viewer's id on the page */
  me: string;
  /** null = the platform didn't say; let a refused write decide */
  canWrite: boolean | null;
}

export interface RemoteMember {
  id: string;
  name: string;
  likes: string[];
  loves: string[];
  nopes: string[];
  updatedAt: number;
  joinedAt: number;
}

const PEOPLE = "people";
const KEY_RE = /^[a-z'-]{1,40}$/;
/** documents are capped at 256 KiB; stay well clear */
const MAX_DOC_CHARS = 200_000;

let roomP: Promise<PageRoom | null> | null = null;

/** The page's shared database and who's viewing, or null when this view can't use them. */
export function pageRoom(): Promise<PageRoom | null> {
  roomP ??= (async () => {
    const claude = window.claude;
    if (!claude || typeof claude.use !== "function") return null;
    const [db, user] = (await Promise.all([claude.use("db"), claude.use("user")])) as [Db | null, User | null];
    if (!db || !user) return null;
    const me = await user.id();
    if (!me) return null;
    return { db, me, canWrite: await user.can("data.write") };
  })().catch(() => null);
  return roomP;
}

/**
 * Has this viewer already let the page use its database? Until they have, the page only
 * touches it when they ask to join in, so nobody gets a permission prompt on arrival.
 */
export async function dbGranted(): Promise<boolean> {
  try {
    const permissions = (await window.claude?.use("permissions")) as { state(name: string): Promise<string> } | null | undefined;
    return (await permissions?.state("db")) === "granted";
  } catch {
    return false;
  }
}

const strings = (v: unknown, max: number): string[] =>
  Array.isArray(v) ? v.filter((k): k is string => typeof k === "string" && KEY_RE.test(k)).slice(0, max) : [];
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

/** Other people's documents are their input: keep only well-formed fields. */
function toMember(id: string, d: Record<string, unknown> | undefined): RemoteMember {
  return {
    id,
    name: typeof d?.name === "string" ? d.name.trim().slice(0, 40) : "",
    likes: strings(d?.likes, 25_000),
    loves: strings(d?.loves, 25_000),
    nopes: strings(d?.nopes, 25_000),
    updatedAt: num(d?.updatedAt),
    joinedAt: num(d?.joinedAt),
  };
}

export async function readMine(room: PageRoom): Promise<RemoteMember | null> {
  const snap = await room.db.collection(PEOPLE).doc(room.me).get();
  return snap.exists ? toMember(snap.id, snap.data()) : null;
}

export async function readAll(room: PageRoom): Promise<RemoteMember[]> {
  const snap = await room.db.collection(PEOPLE).get();
  return snap.docs.filter((d) => d.exists).map((d) => toMember(d.id, d.data()));
}

export function watchPeople(
  room: PageRoom,
  next: (members: RemoteMember[]) => void,
  error: (e: DbError) => void,
): () => void {
  return room.db
    .collection(PEOPLE)
    .onSnapshot((snap) => next(snap.docs.filter((d) => d.exists).map((d) => toMember(d.id, d.data()))), error);
}

export interface MineBody {
  name: string;
  likes: string[];
  loves: string[];
  nopes: string[];
  joinedAt: number;
}

// one write at a time per document: each write waits for the one before it
let queue: Promise<void> = Promise.resolve();

export function writeMine(room: PageRoom, body: MineBody): Promise<void> {
  const doc = { ...body, updatedAt: Date.now() };
  // the oldest passes go first if a long history would overflow the document
  while (JSON.stringify(doc).length > MAX_DOC_CHARS && doc.nopes.length) doc.nopes = doc.nopes.slice(0, Math.floor(doc.nopes.length * 0.8));
  const run = queue.then(() => room.db.collection(PEOPLE).doc(room.me).set(doc));
  queue = run.catch(() => {});
  return run;
}

export function deleteMine(room: PageRoom): Promise<void> {
  const run = queue.then(() => room.db.collection(PEOPLE).doc(room.me).delete());
  queue = run.catch(() => {});
  return run;
}

/** A refused write means this viewer can't save to the page (view-only access). */
export const isAccessError = (e: unknown): boolean => (e as DbError | undefined)?.code === "invalid_argument";
